import React, { MouseEvent, useEffect, useMemo, useState } from 'react';

import {
  Button,
  DateRangePicker,
  Form,
  Input,
  Pagination,
  RefreshIcon,
  Select,
  Text,
  TextButton,
  TypeSelectOption,
  toastService,
} from '@thefarmersfront/kitchen';

import { useQueryClient } from '@tanstack/react-query';
import type { IRowNode } from 'ag-grid-community';

import { PageTitle, Table, TableTopField } from '~/components/common';
import { PageWrapper } from '~/components/layout';
import { ProductSalesModal } from '~/components/product';

import { useModal, useToggle } from '~/hooks/common';
import { productKeys, useGetCodesMenuMenuCodeQuery, useGetSalesChannelQuery } from '~/hooks/product';
import {
  formatDate,
  getDateRangeInfo,
  getProductMappingColumnDefs,
  renderFilterErrorMessage,
  transformToProductSelectOptions as transformToSelectOptions,
} from '~/utils';

import { BREAD_CRUMB, OPTION_ALL, PATH } from '~/constants';
import {
  INITIAL_PRODUCT_PAGE_OFFSET,
  INITIAL_PRODUCT_PAGE_SIZE,
  PRODUCT_PAGE_SIZE,
  TypeProductPageSize,
} from '~/constants/product';

import type {
  TypeGetSalesChannelParams,
  TypeMappingProduct,
  TypeProductMappingFilter,
  TypeProductMappingPageCodesMenu,
} from '~/types/product.types';

import styles from './ProductMappingPage.module.scss';

const ProductMappingPage = () => {
  const queryClient = useQueryClient();

  const codesMenuQuery = useGetCodesMenuMenuCodeQuery<TypeProductMappingPageCodesMenu>({ menuCode: 'SKU_MAPPING' });

  const productKeyword = useMemo(
    () => (codesMenuQuery.data ? transformToSelectOptions(codesMenuQuery.data.klsSalesKeyword.codeItemList) : []),
    [codesMenuQuery.data]
  );

  const productActiveType = useMemo(
    () =>
      codesMenuQuery.data
        ? [OPTION_ALL, ...transformToSelectOptions(codesMenuQuery.data.skuActiveType.codeItemList)]
        : [],
    [codesMenuQuery.data]
  );

  const [productMappingFilter, setProductMappingFilter] = useState<TypeProductMappingFilter>(() => ({
    activeType: OPTION_ALL,
    keyword: productKeyword[0] ?? null,
    keywordValue: '',
    fromDate: null,
    endDate: null,
    currentPage: INITIAL_PRODUCT_PAGE_OFFSET,
    pageSize: INITIAL_PRODUCT_PAGE_SIZE,
  }));

  const [listQueryParams, setListQueryParams] = useState<TypeGetSalesChannelParams | null>(null);

  const salesChannelQuery = useGetSalesChannelQuery({
    params: listQueryParams ?? ({} as TypeGetSalesChannelParams),
    enabled: !!listQueryParams,
  });

  const [selectedSalesData, setSelectedSalesData] = useState<TypeMappingProduct | null>(null);

  const pageOptions = Object.values(PRODUCT_PAGE_SIZE);

  const { isToggle: isFilterOpen, handleToggle: handleFilterToggle } = useToggle({ initialState: true });

  const handleSearch = (filter: TypeProductMappingFilter) => {
    const params: TypeGetSalesChannelParams = {
      ...(filter.activeType.value !== OPTION_ALL.value && { isActive: filter.activeType.value === 'true' }),
      ...(filter.keyword?.value === 'SKU_CODE' && { skuCode: filter.keywordValue?.trim() }),
      ...(filter.keyword?.value === 'SKU_NAME' && { skuName: filter.keywordValue?.trim() }),
      startDate: formatDate(filter.fromDate, 'YYYY-MM-DD'),
      endDate: formatDate(filter.endDate, 'YYYY-MM-DD'),
      limit: filter.pageSize,
      cursor: filter.currentPage,
    };

    queryClient.invalidateQueries({
      queryKey: productKeys.salesChannel(params),
    });

    setProductMappingFilter({
      ...filter,
      keywordValue: filter.keywordValue?.trim(),
    });
    setListQueryParams(params);
  };

  const productSalesDetailModal = useModal({
    onOpen: (node?: IRowNode<TypeMappingProduct>) => {
      if (!node) return;
      setSelectedSalesData(node.data ?? null);
    },
    onClose: () => setSelectedSalesData(null),
    onConfirm: () => {
      setSelectedSalesData(null);
      handleSearch(productMappingFilter);
    },
  });

  const productSalesRegisterModal = useModal({
    onOpen: (node?: IRowNode<TypeMappingProduct>) => {
      if (!node) return;
      setSelectedSalesData(node.data ?? null);
    },
    onClose: () => setSelectedSalesData(null),
    onConfirm: () => {
      setSelectedSalesData(null);
      handleSearch(productMappingFilter);
    },
  });

  const { columnDefs } = getProductMappingColumnDefs({
    onClickAddSalesChannel: productSalesRegisterModal.handleOpenModal,
    onClickViewDetail: productSalesDetailModal.handleOpenModal,
  });

  const handleChangeActiveType = (_: unknown, selectedOption: TypeSelectOption<string>) => {
    setProductMappingFilter({
      ...productMappingFilter,
      activeType: selectedOption,
    });
  };

  const handleChangeKeyword = (_: unknown, selectedOption: TypeSelectOption<string>) => {
    setProductMappingFilter({
      ...productMappingFilter,
      keyword: selectedOption,
      keywordValue: '',
    });
  };

  const handleChangeKeywordValue = (e: React.ChangeEvent<HTMLInputElement>) => {
    setProductMappingFilter({
      ...productMappingFilter,
      keywordValue: e.target.value,
    });
  };

  const handleChangeRegisterDate = (selectedDate: { start: Date | null; end: Date | null } | null) => {
    const startDate = selectedDate?.start;
    const endDate = selectedDate?.end;

    setProductMappingFilter({
      ...productMappingFilter,
      fromDate: startDate,
      endDate,
    });
  };

  const handleSubmitSearch = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    handleSearch({
      ...productMappingFilter,
      currentPage: INITIAL_PRODUCT_PAGE_OFFSET,
    });
  };

  const handleClickReset = () => {
    setProductMappingFilter({
      activeType: OPTION_ALL,
      keyword: productKeyword?.[0] ?? null,
      keywordValue: '',
      fromDate: null,
      endDate: null,
      currentPage: INITIAL_PRODUCT_PAGE_OFFSET,
      pageSize: INITIAL_PRODUCT_PAGE_SIZE,
    });
  };

  const handleChangeLimit = (_: unknown, { value }: TypeSelectOption<TypeProductPageSize>) => {
    handleSearch({
      ...productMappingFilter,
      pageSize: value,
      currentPage: INITIAL_PRODUCT_PAGE_OFFSET,
    });
  };

  const handleClickPage = (e: MouseEvent<HTMLButtonElement>) => {
    const newPage = Number(e.currentTarget.value);

    handleSearch({
      ...productMappingFilter,
      currentPage: newPage - 1,
    });
  };

  useEffect(() => {
    if (!codesMenuQuery.data) return;

    setProductMappingFilter((prev) => ({
      ...prev,
      keyword: prev.keyword ?? productKeyword?.[0] ?? null,
    }));
  }, [codesMenuQuery.data]);

  useEffect(() => {
    if (codesMenuQuery.isError) {
      toastService.addToast({ type: 'error', persist: true, message: renderFilterErrorMessage() });
    }
  }, [codesMenuQuery.isError]);

  return (
    <PageWrapper>
      <PageTitle
        title="상품 판매처 매핑"
        breadcrumbs={BREAD_CRUMB[PATH.PRODUCT_MAPPING]}
        handleFilterToggle={handleFilterToggle}
      />
      {isFilterOpen && (
        <Form className={styles['filter-section']} onSubmit={handleSubmitSearch}>
          <div className={styles['filters-container']}>
            <div className={`${styles['filter-container']} ${styles['operation']}`}>
              <Text className={styles['filter-label']} weight="semi-bold" size="small">
                상품 운영 구분
              </Text>
              <Select
                placeholder="선택"
                options={productActiveType}
                value={productMappingFilter.activeType}
                size="small"
                onChange={handleChangeActiveType}
              />
            </div>
            <div className={`${styles['filter-container']} ${styles['keyword']}`}>
              <Text className={styles['filter-label']} weight="semi-bold" size="small">
                키워드
              </Text>
              <div className={`${styles['input-container']} ${styles['keyword']}`}>
                <Select
                  className={styles['input-select']}
                  placeholder="선택"
                  options={productKeyword}
                  value={productMappingFilter.keyword}
                  size="small"
                  onChange={handleChangeKeyword}
                />
                <Input
                  type="text"
                  value={productMappingFilter.keywordValue}
                  placeholder="입력"
                  size="small"
                  maxLength={200}
                  onChange={handleChangeKeywordValue}
                />
              </div>
            </div>
            <div className={`${styles['filter-container']} ${styles['register']}`}>
              <Text className={styles['filter-label']} weight="semi-bold" size="small">
                상품 등록일
              </Text>
              <DateRangePicker
                allowSingleDateSelect
                size="small"
                placeholder={{
                  start: '시작일',
                  end: '종료일',
                }}
                value={getDateRangeInfo(productMappingFilter.fromDate, productMappingFilter.endDate)}
                onChange={handleChangeRegisterDate}
              />
            </div>
          </div>
          <div className={styles['filter-actions']}>
            <div className={styles['filter-actions-left-field']} />
            <div className={styles['filter-actions-center-field']}>
              <Button type="submit" size="small">
                검색
              </Button>
            </div>
            <div className={styles['filter-actions-right-field']}>
              <TextButton
                className={styles['filter-reset-button']}
                leftIcon={<RefreshIcon size={16} />}
                size="small"
                color="secondary"
                onClick={handleClickReset}
              >
                필터 초기화
              </TextButton>
            </div>
          </div>
        </Form>
      )}

      <div className={styles['view-section']}>
        <TableTopField dataTotalCount={salesChannelQuery.data?.pagination?.listTotal ?? 0} />
        <div className={styles['view-section-table-wrapper']}>
          <Table
            className={styles['view-table']}
            data={salesChannelQuery.data?.data ?? null}
            options={{ columnDefs }}
            rowStatusType={!listQueryParams ? 'INIT' : 'EMPTY'}
          />
        </div>
      </div>
      <div className={styles['view-actions-section']}>
        <div className={styles['view-options']}>
          <Select
            className={styles['limit-select']}
            options={pageOptions}
            value={PRODUCT_PAGE_SIZE[productMappingFilter.pageSize]}
            size="tiny"
            dropdownPlacement="top-left"
            onChange={handleChangeLimit}
          />
        </div>
        {salesChannelQuery.data?.pagination?.listTotal ? (
          <Pagination
            className={styles['pagination']}
            currentPage={productMappingFilter.currentPage + 1}
            totalCount={salesChannelQuery.data.pagination.listTotal}
            pageSize={productMappingFilter.pageSize}
            onClick={handleClickPage}
          />
        ) : null}
      </div>
      {productSalesDetailModal.isOpen && (
        <ProductSalesModal
          mode="detail"
          open={productSalesDetailModal.isOpen}
          data={selectedSalesData}
          onCancel={productSalesDetailModal.handleCloseModal}
          onConfirm={productSalesDetailModal.handleConfirmModal}
        />
      )}
      {productSalesRegisterModal.isOpen && (
        <ProductSalesModal
          mode="register"
          open={productSalesRegisterModal.isOpen}
          data={selectedSalesData}
          onCancel={productSalesRegisterModal.handleCloseModal}
          onConfirm={productSalesRegisterModal.handleConfirmModal}
        />
      )}
    </PageWrapper>
  );
};

export default ProductMappingPage;
